/**
 * Espace destinataire (technicien / partenaire) — Mes rendez-vous.
 * Le destinataire accepte / refuse (motif) un RDV attribué, puis le clôture
 * (réalisé / non effectué). La fiche client n'est révélée qu'après acceptation.
 */
import type { AssignedAppointment } from '@/features/appointments/appointments-api';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { DashboardLayout } from '@/components/dashboard/dashboard-layout';
import {
  appointmentStatusBadge,
  formatSlot,
} from '@/features/appointments/appointment-labels';
import {
  completeAppointment,
  listAssignedAppointments,
  respondAppointment,
} from '@/features/appointments/appointments-api';
import { extractErrorMessage } from '@/features/auth/error-message';
import { useAuth } from '@/lib/auth-context';

export function AssigneeAppointmentsPage() {
  const { user } = useAuth();
  const variant = user?.role === 'partenaire' ? 'partenaire' : 'technicien';
  const qc = useQueryClient();
  const appointments = useQuery({
    queryKey: ['appointments', 'assigned'],
    queryFn: listAssignedAppointments,
  });
  const [reasons, setReasons] = useState<Record<string, string>>({});

  const invalidate = () =>
    qc.invalidateQueries({ queryKey: ['appointments', 'assigned'] });

  const respondMut = useMutation({
    mutationFn: ({ id, decision, reason }: { id: string; decision: 'accepte' | 'refuse'; reason?: string }) =>
      respondAppointment(id, { decision, ...(reason ? { reason } : {}) }),
    onSuccess: () => {
      toast.success('Rendez-vous mis à jour');
      invalidate();
    },
    onError: (e) => toast.error(extractErrorMessage(e)),
  });

  const completeMut = useMutation({
    mutationFn: ({ id, decision, reason }: { id: string; decision: 'realise' | 'non_effectue'; reason?: string }) =>
      completeAppointment(id, { decision, ...(reason ? { reason } : {}) }),
    onSuccess: () => {
      toast.success('Rendez-vous clôturé');
      invalidate();
    },
    onError: (e) => toast.error(extractErrorMessage(e)),
  });

  const busy = respondMut.isPending || completeMut.isPending;

  function renderActions(rdv: AssignedAppointment) {
    if (rdv.status === 'attribue') {
      return (
        <div className="flex flex-wrap items-center justify-end gap-2">
          <Button
            size="sm"
            disabled={busy}
            onClick={() => respondMut.mutate({ id: rdv.id, decision: 'accepte' })}
          >
            Accepter
          </Button>
          <Input
            placeholder="Motif"
            className="h-8 w-28"
            value={reasons[rdv.id] ?? ''}
            onChange={(e) => setReasons({ ...reasons, [rdv.id]: e.target.value })}
          />
          <Button
            size="sm"
            variant="outline"
            className="bg-red-50 text-red-800"
            disabled={busy || !reasons[rdv.id]}
            onClick={() =>
              respondMut.mutate({ id: rdv.id, decision: 'refuse', reason: reasons[rdv.id] })
            }
          >
            Refuser
          </Button>
        </div>
      );
    }
    if (rdv.status === 'accepte') {
      return (
        <div className="flex flex-wrap items-center justify-end gap-2">
          <Button
            size="sm"
            className="bg-green-600 hover:bg-green-700"
            disabled={busy}
            onClick={() => completeMut.mutate({ id: rdv.id, decision: 'realise' })}
          >
            Réalisé
          </Button>
          <Input
            placeholder="Motif"
            className="h-8 w-28"
            value={reasons[rdv.id] ?? ''}
            onChange={(e) => setReasons({ ...reasons, [rdv.id]: e.target.value })}
          />
          <Button
            size="sm"
            variant="outline"
            className="bg-orange-50 text-orange-800"
            disabled={busy || !reasons[rdv.id]}
            onClick={() =>
              completeMut.mutate({ id: rdv.id, decision: 'non_effectue', reason: reasons[rdv.id] })
            }
          >
            Non effectué
          </Button>
        </div>
      );
    }
    return <span className="text-xs text-gray-500">—</span>;
  }

  return (
    <DashboardLayout variant={variant} title="Mes rendez-vous">
      <div className="space-y-4">
        {appointments.data?.length ? (
          appointments.data.map((rdv) => (
            <Card key={rdv.id}>
              <CardHeader className="flex flex-row items-start justify-between gap-4">
                <div>
                  <CardTitle className="text-base">
                    Dossier {rdv.projectReference ?? '—'}
                  </CardTitle>
                  <CardDescription>{formatSlot(rdv.confirmedSlot)}</CardDescription>
                </div>
                <Badge variant="outline" className={appointmentStatusBadge[rdv.status].cls}>
                  {appointmentStatusBadge[rdv.status].label}
                </Badge>
              </CardHeader>
              <CardContent className="space-y-3">
                {rdv.fiche ? (
                  <div className="rounded-md bg-gray-50 p-3 text-sm">
                    <p className="font-medium">
                      {rdv.fiche.client.firstName} {rdv.fiche.client.lastName}
                    </p>
                    <p className="text-gray-600">{rdv.fiche.client.email}</p>
                    <p className="text-gray-600">{rdv.fiche.client.phone}</p>
                  </div>
                ) : (
                  <p className="text-xs text-gray-500">
                    La fiche client sera visible après acceptation.
                  </p>
                )}
                <div className="flex justify-end">{renderActions(rdv)}</div>
              </CardContent>
            </Card>
          ))
        ) : (
          <Card>
            <CardContent className="py-8 text-center text-sm text-gray-500">
              Aucun rendez-vous attribué.
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
}
